import { createContext, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';

export const ShopContext = createContext();

const ShopContextProvider = (props) => {
  const currency = "₹";
  const delivery_fee = 10;
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  const [search, setSearch] = useState("");
  const [showSearch, setShowSearch] = useState(true);
  const [cartItems, setCartItems] = useState({});
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const [reviews,setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [userInfo,setUserInfo] = useState()

  const addToCart = async (itemId, size, color = null, measurements = null, showToast = true) => {
    if (!size) {
      if (showToast) toast.error("Please select size");
      return;
    }
    if (showToast) toast.success("Added to cart");

    let cartData = structuredClone(cartItems);
    if (!cartData[itemId]) cartData[itemId] = {};
    if (!cartData[itemId][size]) cartData[itemId][size] = 0;
    let prevQuantity = 0;
    let newMeasurements = null;
    if (measurements && measurements.H && measurements.W && measurements.measurementUnit) {
      newMeasurements = {
        date: new Date().toISOString(),
        prevCart: Object.fromEntries(
          Object.entries(cartData).map(([id, sizes]) => [id, { ...sizes }])
        ),
        H: measurements.H,
        W: measurements.W,
        measurementUnit: measurements.measurementUnit
      };
    }
    if (color || newMeasurements) {
      if (typeof cartData[itemId][size] === 'object') {
        prevQuantity = cartData[itemId][size].quantity || 0;
      } else if (typeof cartData[itemId][size] === 'number') {
        prevQuantity = cartData[itemId][size];
      }
      cartData[itemId][size] = {
        quantity: prevQuantity + 1,
        color: color || null,
        measurements: newMeasurements
      };
    } else if (typeof cartData[itemId][size] === 'object') {
      cartData[itemId][size].quantity += 1;
    } else {
      cartData[itemId][size] += 1;
    }
    setCartItems(cartData);

    if (token) {
      try {
        const response = await axios.post(
          backendUrl + "/api/cart/add",
          { itemId, size, color, measurements: newMeasurements },
          { headers: { token } }
        );
        if (response.data && response.data.cartData) {
          setCartItems(response.data.cartData);
        }
      } catch (error) {
        if (showToast) toast.error(error.message);
      }
    }
  };

  
  const getUserInfo = useCallback(async () => {
    try {
      const userRaw = localStorage.getItem("user");
      if (userRaw) {
        const user = JSON.parse(userRaw);
        setUserInfo(user);
        return user;
      } 
      setUserInfo({});
      return {};
    } catch (error) {
      setUserInfo({});
      return {};
    }
  }, [token, backendUrl]);


  const getCartCount = () => {
    let totalCount = 0;
    for (const items in cartItems) {
      for (const item in cartItems[items]) {
        try {
          const entry = cartItems[items][item];
          if (typeof entry === 'object') {
            if (entry.quantity > 0) {
              totalCount += entry.quantity;
            }
          } else if (entry > 0) {
            totalCount += entry;
          }
        } catch (error) {
          console.log(error);
        }
      }
    }
    return totalCount;
  };

  const updateQuantity = async (itemId, size, quantity, color = null, measurements = null) => {
    let cartData = structuredClone(cartItems);
    if (typeof cartData[itemId][size] === 'object') {
      cartData[itemId][size].quantity = quantity;
      if (color !== null) cartData[itemId][size].color = color;
      if (measurements !== null) cartData[itemId][size].measurements = measurements;
    } else {
      cartData[itemId][size] = quantity;
    }
    setCartItems(cartData);

    if (token) {
      try {
        const response = await axios.post(
          backendUrl + "/api/cart/update",
          { itemId, size, quantity, color, measurements },
          { headers: { token } }
        );
        if (response.data && response.data.cartData) {
          setCartItems(response.data.cartData);
        }
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  const getCartAmount = () => {
    let totalAmount = 0;
    for (const items in cartItems) {
      let itemInfo = products.find((product) => product._id === items);
      for (const item in cartItems[items]) {
        try {
          const entry = cartItems[items][item];
          let quantity = 0;
          if (typeof entry === 'object') {
            quantity = entry.quantity || 0;
          } else if (typeof entry === 'number') {
            quantity = entry;
          }
          if (quantity > 0 && itemInfo) {
            totalAmount += itemInfo.price * quantity;
          }
        } catch (error) {
          console.log(error);
        }
      }
    }
    return totalAmount;
  };

  const getProductsData = async () => {
    try {
      const response = await axios.get(backendUrl + "/api/product/list");
      if (response.data.success) {
        setProducts(response.data.products);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log(error);
      toast.error(error.message);
    }
  };

  const getReviewsList = async () => {
    try {
      const response = await axios.get(backendUrl + "/api/review/list");
      if (response.data.success) {
        setReviews(response.data.reviews);
      } else{
        toast.error("Something Not Right")
      }
    } catch (error) {
      console.log(error);
    }
  }

  const getUserCart = useCallback(async (token) => {
    if (!token) return;
    try {
      const response = await axios.post(
        backendUrl + "/api/cart/get",
        {},
        { headers: { token } }
      );
      if (response.data && response.data.cartData) {
        setCartItems(response.data.cartData);
      }
    } catch (error) {
      // Optionally handle error
    }
  }, [backendUrl, setCartItems]);

  useEffect(() => {
    getProductsData();
    getReviewsList();
    getUserInfo();
  }, [getUserInfo]);

  useEffect(() => {
    if (!token && localStorage.getItem("token")) {
      setToken(localStorage.getItem("token"));
      getUserCart(localStorage.getItem("token"));
    }
  }, []);

  useEffect(() => {
    if (token) {
      getUserCart(token);
    }
  }, [token, getUserCart]);

  const value = {
    products,
    currency,
    delivery_fee,
    search,
    setSearch,
    showSearch,
    setShowSearch,
    cartItems,
    addToCart, // now supports showToast param
    getCartCount,
    updateQuantity,
    getCartAmount,
    navigate,
    token,
    setToken,
    backendUrl,
    setCartItems,
    reviews,
    userInfo,
    getUserInfo,
    getUserCart
  };

  return (
    <ShopContext.Provider value={value}>{props.children}</ShopContext.Provider>
  );
};

ShopContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ShopContextProvider;
